import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { ModalDialog } from 'components/ModalDialog';
import type {
    BimContractOtherCostBaseData,
    BimWorkOrderCostLimitDistributionData,
    BimWorkOrderObjectDistributionData,
    ContractOtherCostBaseData,
    WorkOrderChangeCostLimitDistributionData,
    WorkOrderChangeOtherCostObjectItem,
    WorkOrderChangeOtherPriceCostData,
    WorkOrderCostLimitDistributionData,
    WorkOrderOtherCostObjectItem,
    WorkOrderOtherPriceCostData,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { ProjectMethodologyEnum } from 'gql/graphql';
import { BimDistributionOtherAllocation } from 'pages/Project/BimProject/WorkOrder/BimOtherPriceModal/components/BimDistributionOtherPrice/BimDistributionOtherAllocation';
import { BimDistributionOtherLimits } from 'pages/Project/BimProject/WorkOrder/BimOtherPriceModal/components/BimDistributionOtherPrice/BimDistributionOtherLimits';
import { tabItemsDistribution, TabsIdsDistribution } from 'pages/Project/Common/types';
import { UerDistributionOtherAllocation } from 'pages/Project/UerProject/WorkOrder/UerDistributionOtherAllocation';
import { Grid, Loader } from 'uikit';

import type { BaseSyntheticEvent, CSSProperties } from 'react';
import { useMemo, useRef } from 'react';
import type {
    Control,
    FieldErrors,
    FieldValues,
    SubmitErrorHandler,
    SubmitHandler,
    UseFormClearErrors,
    UseFormSetError,
    UseFormSetValue,
    UseFormWatch,
} from 'react-hook-form';

import { EditOtherDistributionCommon } from './EditOtherDistributionCommon';
import { EditOtherDistributionLimits } from './EditOtherDistributionLimits';

import Styles from './EditOtherDistribution.module.css';

interface IEditDistributionProps<T> {
    handleCancel: () => void;
    onSubmit: (formData: any) => void;
    isLoading: boolean;
    activeTab: { label: string; id: TabsIdsDistribution };
    handleTabChange: ({ value }: { value: { label: string; id: TabsIdsDistribution } }) => void;
    isDataFetching: boolean;
    isCalculating?: boolean;
    workOrderCommonData?: ContractOtherCostBaseData | null;
    currentConstructionTypeId?: number;
    bimWorkOrderOtherDistributionData?: T | null;
    uerWorkOrderOtherDistributionData?:
        | {
              objects: (WorkOrderOtherCostObjectItem | WorkOrderChangeOtherCostObjectItem)[] | undefined;
              objectDistributionData: (WorkOrderOtherPriceCostData | WorkOrderChangeOtherPriceCostData)[] | undefined;
              objectLimitsData:
                  | WorkOrderCostLimitDistributionData
                  | WorkOrderChangeCostLimitDistributionData
                  | undefined;
          }[]
        | null
        | undefined;
    workOrderLimitsData?: WorkOrderCostLimitDistributionData[] | WorkOrderChangeCostLimitDistributionData[] | null;
    isOpenWarningDialog: boolean;
    handleSubmitWarningDialog: () => void;
    handleCancelWarningDialog: () => void;
    formControl: Control;
    formHandleSubmit: (
        onValid: SubmitHandler<FieldValues>,
        onInvalid?: SubmitErrorHandler<FieldValues>,
    ) => (e?: BaseSyntheticEvent) => Promise<void>;
    setValue?: UseFormSetValue<any>;
    isWorkOrderChange?: boolean;
    statusWorkOrder?: WorkOrderStatusEnum;
    watch?: UseFormWatch<any>;
    workOrderOtherPriceId?: number;
    contractOtherCostId?: number;
    setError?: UseFormSetError<any>;
    clearErrors?: UseFormClearErrors<any>;
    errors?: FieldErrors<any>;
    workOrderChangeCompositeIds?: string;
    isFrameContract?: boolean;
    costRounding: number;
}

export const invisibleStyle = { visibility: 'hidden', position: 'absolute', overflow: 'hidden', maxHeight: 0 };

const fieldsToCheck = {
    [TabsIdsDistribution.LIMITS]: ['includedCost'],
    [TabsIdsDistribution.TREATY]: ['isIncluded'],
    [TabsIdsDistribution.DISTRIBUTION]: ['objectDistribution'],
};

export const EditOtherDistributionModal = <
    T extends {
        contractOtherCost: BimContractOtherCostBaseData;
        limit: BimWorkOrderCostLimitDistributionData;
        objectDistribution: Array<BimWorkOrderObjectDistributionData>;
    },
>(
    props: IEditDistributionProps<T>,
) => {
    const formRef = useRef<HTMLFormElement>(null);
    const { methodology } = useProjectParams();

    const {
        handleCancel,
        onSubmit,
        isLoading,
        activeTab,
        handleTabChange,
        isDataFetching,
        isCalculating,
        bimWorkOrderOtherDistributionData,
        uerWorkOrderOtherDistributionData,
        workOrderCommonData,
        currentConstructionTypeId,
        workOrderLimitsData,
        isOpenWarningDialog,
        handleSubmitWarningDialog,
        handleCancelWarningDialog,
        formControl,
        formHandleSubmit,
        setValue,
        isWorkOrderChange = false,
        statusWorkOrder,
        watch,
        workOrderOtherPriceId,
        workOrderChangeCompositeIds,
        contractOtherCostId,
        setError,
        clearErrors,
        errors,
        isFrameContract,
        costRounding,
    } = props;

    const isMethodologyUER = methodology?.toUpperCase() === ProjectMethodologyEnum.Uer;
    const isMethodologyBIM = methodology?.toUpperCase() === ProjectMethodologyEnum.Bim;

    const commonTabStyle = useMemo(
        () => (activeTab.id === TabsIdsDistribution.COMMON ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const limitsTabStyle = useMemo(
        () =>
            activeTab.id === TabsIdsDistribution.LIMITS || activeTab.id === TabsIdsDistribution.TREATY
                ? undefined
                : invisibleStyle,
        [activeTab.id],
    );

    const distributionTabStyle = useMemo(
        () => (activeTab.id === TabsIdsDistribution.DISTRIBUTION ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const bimTabItems = tabItemsDistribution.filter((i) => i.id !== TabsIdsDistribution.LIMITS);
    const uerTabItems = tabItemsDistribution.filter((i) => i.id !== TabsIdsDistribution.TREATY);
    const currentTabItems = isMethodologyBIM ? bimTabItems : uerTabItems;

    return (
        <ModalDialog
            title="Распределение прочих затрат"
            isOpen
            hasOverlay
            withTitleCloseButton
            onClose={handleCancel}
            onConfirm={formHandleSubmit(onSubmit)}
            isCommit={isLoading}
            isOnClickOutsideDisable
            btnConfirmDisabled={!!Object.keys(errors ?? {}).length}
            className={Styles.modalDistributionOther}
            bodyOverflow="hidden"
        >
            <div className={Styles.innerBlock}>
                <ControlledTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    items={currentTabItems}
                    size="s"
                    control={formControl}
                    fieldsToCheck={fieldsToCheck}
                />
                <form onSubmit={formHandleSubmit(onSubmit)} ref={formRef}>
                    <div className={Styles.tabsContent}>
                        {isDataFetching && <Loader style={{ position: 'absolute', top: '50%' }} />}
                        {!isDataFetching && (
                            <>
                                <Grid style={commonTabStyle as CSSProperties}>
                                    <EditOtherDistributionCommon
                                        isCalculating={isCalculating}
                                        workOrderCommonData={workOrderCommonData}
                                        currentConstructionTypeId={currentConstructionTypeId}
                                        bimWorkOrderOtherCommonData={
                                            bimWorkOrderOtherDistributionData?.contractOtherCost
                                        }
                                        isUer={isMethodologyUER}
                                        isBim={isMethodologyBIM}
                                    />
                                </Grid>
                                <Grid style={limitsTabStyle as CSSProperties}>
                                    {isMethodologyUER && (
                                        <EditOtherDistributionLimits
                                            control={formControl}
                                            isCalculating={isCalculating}
                                            workOrderLimitsData={workOrderLimitsData}
                                            isWorkOrderChange={isWorkOrderChange}
                                            isVisible={!limitsTabStyle}
                                            costRounding={costRounding}
                                        />
                                    )}
                                    {isMethodologyBIM && (
                                        <BimDistributionOtherLimits
                                            control={formControl}
                                            distributionData={bimWorkOrderOtherDistributionData}
                                            statusWorkOrder={statusWorkOrder}
                                            setValue={setValue}
                                            isFrameContract={isFrameContract}
                                            isWorkOrderChange={isWorkOrderChange}
                                        />
                                    )}
                                </Grid>
                                <Grid style={distributionTabStyle as CSSProperties}>
                                    {isMethodologyUER && (
                                        <UerDistributionOtherAllocation
                                            control={formControl}
                                            isWorkOrderChange={isWorkOrderChange}
                                            uerWorkOrderOtherDistributionData={uerWorkOrderOtherDistributionData}
                                            costRounding={costRounding}
                                            workOrderOtherPriceId={workOrderOtherPriceId}
                                            workOrderChangeCompositeIds={workOrderChangeCompositeIds}
                                            contractOtherCostId={workOrderCommonData?.id}
                                            watch={watch}
                                            setValue={setValue}
                                            isCalculating={isCalculating}
                                        />
                                    )}
                                    {isMethodologyBIM && (
                                        <BimDistributionOtherAllocation
                                            control={formControl}
                                            distributionData={bimWorkOrderOtherDistributionData}
                                            setValue={setValue}
                                            watch={watch}
                                            workOrderOtherPriceId={workOrderOtherPriceId}
                                            contractOtherCostId={contractOtherCostId}
                                            setError={setError}
                                            clearErrors={clearErrors}
                                            workOrderChangeCompositeIds={workOrderChangeCompositeIds}
                                            isWorkOrderChange={isWorkOrderChange}
                                            isCalculating={isCalculating}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}
                    </div>
                </form>
            </div>
            <ConfirmationDialog
                textHeader="ВВОД ДАННЫХ"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenWarningDialog}
                onSubmit={handleSubmitWarningDialog}
                onClose={handleCancelWarningDialog}
            />
        </ModalDialog>
    );
};

EditOtherDistributionModal.displayName = 'EditOtherDistributionModal';
